import { createNestablePublicClientApplication, PublicClientApplication } from '@azure/msal-browser';
/* global Office */

/**
 * Class to handle authentication using MSAL
 * Note: The scopes used for authentication allow us to validate the token on the backend
 */
export default class MsalAuth {
  /** */
  constructor() {
    this.pca = null;
  }

  /**
   * This function retrieves a token using Nested App Authentication
   * @return {String} accessToken
   */
  async getTokenUsingNestable() {
    try {
      try {
        this.pca = await createNestablePublicClientApplication(this.getMsalConfig());
      } catch (e) {
        console.log('Error creating nestable pca: ', e);
      }
      const authContext = await Office.auth.getAuthContext();
      const username = authContext.userPrincipalName;
      const tenantId = authContext.tenantId;
      const localAccountId = authContext.userObjectId;

      const account = this.pca.getAccount({ username, tenantId, localAccountId });
      let accessToken = null;
      let userAccount = null;
      try {
        userAccount = await this.pca.acquireTokenSilent({ scopes: [`${process.env.AZURE_CLIENT_ID}/.default`], account });
        accessToken = userAccount.accessToken;
      } catch (e) {
        console.log('Error acquiring token via NAA silently: ', e);
      }
      if (!accessToken?.length) {
        // Token acquisition failed, so we need to manually fetch a token using a pop-up
        userAccount = await this.pca.acquireTokenPopup({ scopes: [`${process.env.AZURE_CLIENT_ID}/.default`] });
        accessToken = userAccount?.accessToken;
      }

      // If we still don't have an access token, then we can't proceed
      if (!accessToken?.length) {
        console.log('No access token available using NAA.');
      }
      return { accessToken, expiration: userAccount?.expiresOn };
    } catch (e) {
      console.error('Error fetching token using NAA: ', e);
      return null;
    }
  }

  /**
   * This function retrieves a token using MSAL Legacy App Authentication
   * Should be invoked when NAA is not available
   * @param {Function} callback
   * @param {Function} errorCallback
   */
  async getTokenUsingLegacy(callback, errorCallback) {
    this.pca = new PublicClientApplication(this.getMsalConfig());
    await this.pca.initialize(this.getMsalConfig());
    const account = this.pca.getActiveAccount();

    if (account) {
      try {
        console.log('Auth Logs: Active account found; acquiring token silently...');
        const tokenResponse = await this.pca.acquireTokenSilent({ scopes: [`${process.env.AZURE_CLIENT_ID}/.default`] });
        callback({ accessToken: tokenResponse.accessToken, expiration: tokenResponse.expiresOn });
        return;
      } catch (e) {
        console.log('Error acquiring token silently via legacy, will now attempt to log in using dialog: ', e);
      }
    }

    let dialog;
    const pca = this.pca;
    Office.context.ui.displayDialogAsync(`${process.env.PLUGIN_URL}/dialogAuth.html`, { height: 30, width: 30, displayInIframe: true }, function (asyncResult) {
      if (asyncResult.status !== Office.AsyncResultStatus.Succeeded) {
        errorCallback(asyncResult.error);
        return;
      }
      dialog = asyncResult.value;
      dialog.addEventHandler(Office.EventType.DialogMessageReceived, (data) => {
        const message = JSON.parse(data.message);


        if (message.type !== 'microsoft') {
          return;
        }

        if (message.account) {
          // This should let us retrieve the token silently next time
          pca.setActiveAccount(message.account);
        }

        dialog.close();

        if (message.status === 'failure') {
          errorCallback(message.result);
          return;
        }

        callback({ accessToken: message.token, expiration: message.expiration });
      });
    });
  }

  /**
   * This function retrieves MSAL configuration
   * @return {Object} msalConfig
   */
  getMsalConfig() {
    return {
      auth: {
        clientId: process.env.AZURE_CLIENT_ID,
        authority: 'https://login.microsoftonline.com/common',
        redirectUri: `${process.env.PLUGIN_URL}/dialogAuth.html`,
      },
      cache: {
        cacheLocation: 'localStorage',
        storeAuthStateInCookie: true,
      },
    };
  }
}
